/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface QuestionnaireQuestionResponseNoteProps {
  id: string;
  questionnaireQuestionResponseId: string;
  authorId: string;
  dateAuthored: Date;
  note: string;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class QuestionnaireQuestionResponseNoteRecord extends Record<QuestionnaireQuestionResponseNoteProps>(
  {
    id: null as any,
    questionnaireQuestionResponseId: null as any,
    authorId: null as any,
    dateAuthored: null as any,
    note: null as any,
    companyId: null as any,
    dateLastUpdated: null as any,
    dateCreated: null as any,
    dateDeleted: null as any,
    lastUpdatedById: null as any,
  },
) {
  public static fromJS(json: {
    id: string;
    questionnaireQuestionResponseId: string;
    authorId: string;
    dateAuthored: string | number | Date;
    note: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): QuestionnaireQuestionResponseNoteRecord {
    if (json == null) {
      throw new Error('QuestionnaireQuestionResponseNoteRecord.fromJS: parameter cannot be null');
    }
    return new QuestionnaireQuestionResponseNoteRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'QuestionnaireQuestionResponseNoteJson.id',
      ),
      questionnaireQuestionResponseId: getFromOrThrow(
        json,
        (j) =>
          propValueOrDefault(
            j,
            'questionnaireQuestionResponseId',
            '00000000-0000-0000-0000-000000000000',
          ),
        'QuestionnaireQuestionResponseNoteJson.questionnaireQuestionResponseId',
      ),
      authorId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'authorId', '00000000-0000-0000-0000-000000000000'),
        'QuestionnaireQuestionResponseNoteJson.authorId',
      ),
      dateAuthored: new Date(
        getFromOrThrow(
          json,
          (j) => propValueOrDefault(j, 'dateAuthored', '0001-01-01T00:00:00.00Z'),
          'QuestionnaireQuestionResponseNoteJson.dateAuthored',
        ),
      ),
      note: getFromOrThrow(json, (j) => j.note, 'QuestionnaireQuestionResponseNoteJson.note'),
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'QuestionnaireQuestionResponseNoteJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    questionnaireQuestionResponseId: string;
    authorId: string;
    dateAuthored: string | number | Date;
    note: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [QuestionnaireQuestionResponseNoteRecord, Error[]] {
    if (json == null) {
      throw new Error(
        'QuestionnaireQuestionResponseNoteRecord.fromJSUnsafe: parameter cannot be null',
      );
    }
    const errors: Error[] = [];

    const record = new QuestionnaireQuestionResponseNoteRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'QuestionnaireQuestionResponseNoteJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      questionnaireQuestionResponseId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) =>
              propValueOrDefault(
                j,
                'questionnaireQuestionResponseId',
                '00000000-0000-0000-0000-000000000000',
              ),
            'QuestionnaireQuestionResponseNoteJson.questionnaireQuestionResponseId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      authorId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'authorId', '00000000-0000-0000-0000-000000000000'),
            'QuestionnaireQuestionResponseNoteJson.authorId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      dateAuthored: tryOrDefault(
        () =>
          new Date(
            getFromOrThrow(
              json,
              (j) => propValueOrDefault(j, 'dateAuthored', '0001-01-01T00:00:00.00Z'),
              'QuestionnaireQuestionResponseNoteJson.dateAuthored',
            ),
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      note: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.note, 'QuestionnaireQuestionResponseNoteJson.note'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'QuestionnaireQuestionResponseNoteJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: QuestionnaireQuestionResponseNoteProps) {
    super(props);
    if (props == null) {
      throw new Error(
        'QuestionnaireQuestionResponseNoteRecord constructor parameter cannot be null',
      );
    }
  }
}

export abstract class QuestionnaireQuestionResponseNoteViewModelBase
  implements QuestionnaireQuestionResponseNoteProps
{
  protected readonly _props: QuestionnaireQuestionResponseNoteProps;
  private _json?: QuestionnaireQuestionResponseNoteProps;

  get id(): string {
    return this._props.id;
  }
  get questionnaireQuestionResponseId(): string {
    return this._props.questionnaireQuestionResponseId;
  }
  get authorId(): string {
    return this._props.authorId;
  }
  get dateAuthored(): Date {
    return this._props.dateAuthored;
  }
  get note(): string {
    return this._props.note;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: QuestionnaireQuestionResponseNoteProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        questionnaireQuestionResponseId: this._props.questionnaireQuestionResponseId,
        authorId: this._props.authorId,
        dateAuthored: this._props.dateAuthored,
        note: this._props.note,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllQuestionnaireQuestionResponseNoteEntities = (state: AppState) =>
  state.entityStores.questionnaireQuestionResponseNote.entities;

export const selectQuestionnaireQuestionResponseNoteById = (id: string) => (state: AppState) =>
  state.entityStores.questionnaireQuestionResponseNote.entities.get(id);

export const selectQuestionnaireQuestionResponseNoteMetadataById =
  (id: string) => (state: AppState) =>
    state.entityStores.questionnaireQuestionResponseNote.meta.get(id);

export const selectQuestionnaireQuestionResponseNoteAndMetadataById = (id: string) =>
  createSelector(
    selectQuestionnaireQuestionResponseNoteById(id),
    selectQuestionnaireQuestionResponseNoteMetadataById(id),
    (questionnaireQuestionResponseNote, meta) => ({ questionnaireQuestionResponseNote, meta }),
  );

export function selectQuestionnaireQuestionResponseNoteEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllQuestionnaireQuestionResponseNoteEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, QuestionnaireQuestionResponseNoteRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectQuestionnaireQuestionResponseNoteEntitiesByQuestionnaireQuestionResponseId(
  questionnaireQuestionResponseId: string,
) {
  return createSelector(selectAllQuestionnaireQuestionResponseNoteEntities, (entities) =>
    entities
      .filter((e) => e.questionnaireQuestionResponseId === questionnaireQuestionResponseId)
      .valueSeq(),
  );
}

export function selectQuestionnaireQuestionResponseNoteEntitiesByAuthorId(authorId: string) {
  return createSelector(selectAllQuestionnaireQuestionResponseNoteEntities, (entities) =>
    entities.filter((e) => e.authorId === authorId).valueSeq(),
  );
}

export function selectQuestionnaireQuestionResponseNoteEntitiesByDateAuthored(dateAuthored: Date) {
  return createSelector(selectAllQuestionnaireQuestionResponseNoteEntities, (entities) =>
    entities.filter((e) => e.dateAuthored === dateAuthored).valueSeq(),
  );
}

export function selectQuestionnaireQuestionResponseNoteEntitiesByNote(note: string) {
  return createSelector(selectAllQuestionnaireQuestionResponseNoteEntities, (entities) =>
    entities.filter((e) => e.note === note).valueSeq(),
  );
}

export function selectQuestionnaireQuestionResponseNoteEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllQuestionnaireQuestionResponseNoteEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
